import type { CreatePaymentIntentResponse } from '../../types/generated/models/create-payment-intent-response'

/* ── State interface ── */

export interface CheckoutState {
  clientSecret: string | null
  stripePublishableKey: string | null
  totalMonthlyPrice: number | null
  currency: string
  deviceCount: number | null
  subscriptionTier: string | null
  discountCode: string | null
  loading: boolean
  processing: boolean
  error: boolean
  errorMessage: string
  paymentSuccess: boolean
  paymentError: string
  paymentIntentId: string | null
  paymentIntentStatus: string | null
}

/* ── Action Types ── */

export const INITIALIZE_CHECKOUT = 'INITIALIZE_CHECKOUT'
export const INITIALIZE_CHECKOUT_SUCCESS = 'INITIALIZE_CHECKOUT_SUCCESS'
export const INITIALIZE_CHECKOUT_FAIL = 'INITIALIZE_CHECKOUT_FAIL'
export const RESET_CHECKOUT = 'RESET_CHECKOUT'
export const SET_PAYMENT_PROCESSING = 'SET_PAYMENT_PROCESSING'
export const SET_PAYMENT_SUCCESS = 'SET_PAYMENT_SUCCESS'
export const SET_PAYMENT_ERROR = 'SET_PAYMENT_ERROR'

/* ── Initial State ── */

const initialState: CheckoutState = {
  // Checkout initialization
  clientSecret: null,
  stripePublishableKey: null,
  totalMonthlyPrice: null,
  currency: 'gbp',

  // Device/pricing info
  deviceCount: null,
  subscriptionTier: null,
  discountCode: null,

  // UI State
  loading: false,
  processing: false,
  error: false,
  errorMessage: '',
  paymentSuccess: false,
  paymentError: '',

  // Payment Intent
  paymentIntentId: null,
  paymentIntentStatus: null
}

/* ── Reducer ── */

export default function checkoutReducer(
  state: CheckoutState = initialState,
  action: any
): CheckoutState {
  switch (action.type) {
    case INITIALIZE_CHECKOUT:
      return {
        ...state,
        loading: true,
        error: false,
        errorMessage: '',
        paymentSuccess: false,
        paymentError: ''
      }

    case INITIALIZE_CHECKOUT_SUCCESS: {
      // The backend returns amount as monthly price in pence (e.g., 833 = £8.33/month)
      const payloadData = action.payload?.data as CreatePaymentIntentResponse | undefined
      let totalMonthly: number | null = null
      if (payloadData) {
        // If no explicit totals, amount is in pence (smallest currency unit)
        if (payloadData.amountIncludingTax != null) {
          totalMonthly = Number(payloadData.amountIncludingTax) / 100
        }
      }

      return {
        ...state,
        loading: false,
        clientSecret: payloadData?.clientSecret ?? null,
        stripePublishableKey: payloadData?.stripePublishableKey ?? null,
        totalMonthlyPrice: totalMonthly,
        currency: payloadData?.currency || 'gbp',
        deviceCount: payloadData?.deviceCount ?? null,
        subscriptionTier: payloadData?.subscriptionTier ?? null,
        discountCode: null,
        paymentIntentId: null,
        paymentIntentStatus: null,
        error: false,
        errorMessage: ''
      }
    }

    case INITIALIZE_CHECKOUT_FAIL: {
      // Extract error message from axios error response structure
      let errorMsg = 'Failed to initialize checkout'
      if (action.payload?.data?.error) {
        errorMsg = action.payload.data.error
      } else if (action.payload?.message) {
        errorMsg = action.payload.message
      } else if (typeof action.payload === 'string') {
        errorMsg = action.payload
      }
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: errorMsg,
        clientSecret: null,
        stripePublishableKey: null,
        totalMonthlyPrice: null
      }
    }

    case SET_PAYMENT_PROCESSING:
      return {
        ...state,
        processing: true,
        paymentError: '',
        paymentSuccess: false
      }

    case SET_PAYMENT_SUCCESS:
      return {
        ...state,
        processing: false,
        paymentSuccess: true,
        paymentIntentStatus: 'succeeded',
        paymentError: ''
      }

    case SET_PAYMENT_ERROR:
      return {
        ...state,
        processing: false,
        paymentSuccess: false,
        paymentError: action.payload.error || 'Payment failed'
      }

    case RESET_CHECKOUT:
      return initialState

    default:
      return state
  }
}
